import React, { PureComponent } from "react";

import { toast } from "react-toastify";

import { Popover, OverlayTrigger } from "react-bootstrap";
import { Button, ButtonGroup, Accordion, Icon } from "aible";

import { isEnabled } from "utils/featureFlags";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

import ProjectHelpContainer from "containers/UnifiedProject/Help/ProjectHelp";
import ProjectHelpItem from "components/UnifiedProject/Help/ProjectHelpItem";
import AdvancedDeploy from "components/Project/AdvancedDeploy";

import SalesforceLogo from "images/datasource-sf.png";
import TableauLogo from "images/tableau.png";
import BoomiLogo from "images/boomi.svg";
import PowerBILogo from "images/New_Power_BI_Logo.svg";
import FileLogo from "images/csv.svg";

import styles from "./style.module.css";

export default class Integrate extends PureComponent {
  constructor(props) {
    super(props);

    this.state = { isCreatingDataset: false, isMonitorScoring: false };
  }

  getIsMonitorScoring() {
    const { isMonitorScoring } = this.state;
    return isEnabled("allow-monitor-bulk-scoring") && isMonitorScoring;
  }

  getCURL(deployment) {
    return `curl --request POST \\
  --url ${deployment.endpoint_url} \\
  --header 'content-type: application/json' \\
  ${Object.keys(deployment.endpoint_headers || {}).map(
    (key) => `--header '${key}: ${deployment.endpoint_headers[key]}' \\`
  )}
  --data '{"data":"FIELD_1,FIELD_2,FIELD_3\\nVALUE_1,VALUE_2,VALUE_3"}'
`;
  }

  getOpenAPI(id, url) {
    if (url.endsWith("/scores")) {
      url = url.slice(0, -6);
    }

    if (url.endsWith("/v1/")) {
      url = url.slice(0, -3);
    }

    if (!url.endsWith("/")) {
      url = url + "/";
    }

    url = url + "v1";

    const isAzure = url.includes("azure");

    const security = [
      ...(isAzure ? [] : [{ BasicAuth: [] }]),
      {
        BearerToken: []
      }
    ];

    const securitySchemas = {
      BearerToken: {
        type: "http",
        scheme: "bearer"
      }
    };

    if (!isAzure) {
      securitySchemas["BasicAuth"] = {
        type: "http",
        scheme: "basic"
      };
    }

    const dataResponse = {
      description: `
Returned when the results are ready. In this case the body will contain the scored results. The scored results are the same as the input CSV but with the following additional columns:

| Column                   | Value      | Notes |
| ------------------------ | ---------- | ----- |
| predict                  | 0.0 or 1.0 | The Aible recommendation for classification use-cases. Undefined for regression use-cases. |
| predict_score            | Numeric    | The score (0.0 to 1.0)  for classification use-cases or the numeric value for regression use-cases. |
| scoring_warning_messages | Text       | Per record warnings. Normally blank |
| model_name               | Text       | The model used to generate the prediction |
| project_segment_id       | Numeric    | The subgroup ID for each record |
| job_id                   | Numeric    | The model job ID |
| threshold                | Numeric    | The positive threshold for classification use-cases |
| Driver 1 . . . 5         | Text       | The top 5 columns that impact the prediction score. |
| Impact 1 . . . 5         | Numeric    | The impact values for the top 5 columns |
`,
      content: {
        "text/csv": {
          schema: {
            type: "string"
          }
        }
      }
    };

    const statusResponse = {
      description: "Returned if the results are not ready after 20 seconds and scoring is still in progress.",
      content: {
        "application/json": {
          schema: {
            $ref: "#/components/schemas/ScoringJobStatus"
          }
        }
      }
    };

    const docs = {
      openapi: "3.0.2",
      info: {
        title: "Aible REST API",
        version: "1.0"
      },
      servers: [{ url }],
      security: security,
      tags: [
        {
          name: "Scoring",
          description: "Generate predictions from your deployed projects"
        }
      ],
      paths: {
        "/scores/projects/{projectId}": {
          post: {
            description: "Create a new scoring job from raw CSV data",
            tags: ["Scoring"],
            parameters: [
              {
                name: "projectId",
                in: "path",
                description: "The id of a project with a deployed scenario.",
                required: true,
                example: id,
                schema: {
                  type: "integer"
                }
              },
              {
                name: "monitor",
                in: "query",
                description: "Toggles whether or not the results of this prediction are stored for monitoring",
                required: false,
                schema: {
                  type: "boolean",
                  default: false
                }
              }
            ],
            requestBody: {
              description: `
The body should contain the record(s) to be scored in text formatted as CSV. The CSV must include a header line identifying the columns and one line for each record being scored, with the lines separated by a carriage return.

Additional notes:
- You don't have to specify every column in the model, missing columns will be given a default level.
- Every column listed in the header must be represented in the rows but the value can be blank (e.g., the State/Region and Year Founded are missing in the 3rd row)
- You can include additional columns that aren't part of the model, these will be ignored and passed through to the output. This can be useful to send a unique identifier that won't be used by the AI but is needed to write the results to a downstream system.
- It is best practice to surround string values with double quotes.
- Aible will parse every value into the type specified for the column in the model; e.g., the "Year Founded" values are included in the model as a numeric field, even though they are quoted as strings in the CSV.
-The payload can be up to 3MB. This can typically accommodate several thousand records.
              `,
              required: true,
              content: {
                "text/csv": {
                  schema: {
                    type: "string",
                    example:
                      '"Id", "Lead Source", "State/Region", "Industry", "Create Date", "Year Founded"\n"123", "List Import", "Greater Manchester", "APPAREL_FASHION", "03/10/2022", "1956"\n"456", "Incoming", "New York", "TELECOMMUNICATIONS", "03/09/2022", "1998"\n"789", "Incoming",, "PHARMA", "8/03/2022",'
                  }
                }
              }
            },
            responses: {
              200: dataResponse,
              202: statusResponse
            }
          }
        },
        "/scores/{scoreId}": {
          get: {
            description: "Get an existing scoring job",
            tags: ["Scoring"],
            parameters: [
              {
                name: "scoreId",
                in: "path",
                description: "Id of the scoring job",
                required: true,
                schema: {
                  type: "string"
                }
              }
            ],
            responses: {
              200: dataResponse,
              202: statusResponse
            }
          }
        }
      },
      components: {
        securitySchemes: securitySchemas,
        schemas: {
          ScoringJobStatus: {
            type: "object",
            properties: {
              id: {
                type: "string"
              },
              status: {
                type: "string",
                description: "Status Values: [INPROGRESS, COMPLETED]",
                enum: ["INPROGRESS", "COMPLETED"]
              }
            }
          }
        }
      }
    };

    return JSON.stringify(docs);
  }

  getPython(deployment) {
    const headers = {
      "content-type": "application/json",
      ...(deployment.endpoint_headers || {})
    };

    const headersString = Object.keys(headers)
      .map((key) => `\n  '${key}': "${headers[key]}"`)
      .join(",");

    return `import time
import requests
import pandas as pd
from io import StringIO

input_df = # some pandas data frame

payload = {"data": input_df.to_csv()}

headers = {${headersString}
}

url = "${deployment.endpoint_url}"

r = requests.post(url, headers=headers, json=payload)

if r.status_code == 202:
    response = r.json()
else:
    raise ValueError(f"{r.status_code}: request failed")

while response["status"] == "INPROGRESS":
    time.sleep(2)

    r = requests.post(response["url"], headers=headers, json="")

    if r.status_code == 200:
        response = r.json()
    else:
        raise ValueError(f"{r.status_code}: request failed")

df = pd.read_csv(StringIO(response["predictions"]))`;
  }

  renderDeployAdvanced() {
    return (
      <React.Fragment>
        <Accordion
          header={
            <div className={styles.deployAdvancedDataTitle}>
              <Icon type="toolsPurple" style={{ marginRight: "8px" }} size={16} />
              {"Advanced Settings"}
            </div>
          }
          shrinkHeader
        >
          <AdvancedDeploy {...this.props} />
        </Accordion>
      </React.Fragment>
    );
  }

  renderMonitorScoring() {
    const { isScoringFile } = this.props;
    const { isMonitorScoring } = this.state;

    if (!isEnabled("allow-monitor-bulk-scoring") || isScoringFile) {
      return <React.Fragment />;
    }

    return (
      <div className={styles.monitorScoringWrapper}>
        <div className={styles.monitorScoringTitle}>
          <input
            type="checkbox"
            style={{ margin: "0px 4px 2px 0px" }}
            checked={isMonitorScoring}
            onChange={() => this.setState({ isMonitorScoring: !isMonitorScoring })}
          />
          <div>{"Monitor"}</div>
        </div>
      </div>
    );
  }

  render() {
    const {
      isNext,
      deployment,
      isScoringFile,
      goToTableauHelp,
      goToBoomiHelp,
      goToSalesforceHelp,
      scoreFile,
      createDatasetFromMonitor,
      accountAPIEndpoint,
      id,
      businessMode,
      selectedTenant
    } = this.props;

    const { isCreatingDataset } = this.state;

    if (!deployment) {
      return (
        <div className={styles.deploy + " " + styles.deployed}>
          <div className={styles.deployHeader} style={{ fontSize: "20px", marginBottom: "20px" }}>
            {"No deployment found! Please deploy a scenario."}
          </div>
          <div style={{ fontSize: "13px", fontWeight: "default" }}>
            {`Navigate to the “I want to deploy the current AI” hex, then press the “Review and Deploy AI” hex in the top position, then select the scenario you would like to deploy from the dropdown and press “Deploy”`}
          </div>
        </div>
      );
    }

    const isError = !deployment || deployment.status === "ERROR";
    const isDeploying = deployment && !deployment.endpoint_url;

    const headerText = isDeploying
      ? "Setting up your project for integration..."
      : "Your project is ready to be integrated!";

    return (
      <div className={styles.deploy + " " + styles.deployed}>
        <ProjectHelpContainer title={headerText}>
          <ProjectHelpItem type={"box"}>
            {
              "Add predictions and recommendations to your business processes by adding a pre-built or custom integration. "
            }
          </ProjectHelpItem>
          <ProjectHelpItem type={"settings"}>
            {"Changing the end-user individualization modifies how far a user can change their AI " +
              "behavior (more aggressive or conservative predictions) from the organizations recommended level"}
          </ProjectHelpItem>
        </ProjectHelpContainer>
        <div className={styles.deployFlex} style={{ marginTop: "20px" }}>
          <div className={styles.deployLeft}>
            <div>
              <div className={styles.deployHeader}>{"Pre-Built Integrations"}</div>
              <div className={styles.deploySubHeader}>
                {"Get up and running with predictions by using our pre-built integrations."}
              </div>
            </div>
            <div
              className={styles.deployBoxHolder}
              style={
                isNext
                  ? //? { height: "50vh", overflow: "auto" }
                    { overflow: "auto" }
                  : businessMode
                    ? { paddingBottom: "20px" }
                    : {}
              }
            >
              <div className={styles.deployBox}>
                <img src={BoomiLogo} className={styles.deployIcon + " " + styles.boomiIcon} />
                <div className={styles.deployBoxHeader}>{"Boomi"}</div>
                <div className={styles.deployBoxContent}>
                  {"Drive AI projects from data acquisition to model deployment with the Aible AI connector. "}
                  <a onClick={() => goToBoomiHelp()}>{"Learn more"}</a>
                </div>
              </div>
              <div className={styles.deployBox}>
                <img src={SalesforceLogo} className={styles.deployIcon} />
                <div className={styles.deployBoxHeader}>{"Salesforce"}</div>
                <div className={styles.deployBoxContent}>
                  {"See predictions in context while viewing a record in Salesforce"}
                </div>
                {/* <a onClick={() => goToSalesforceHelp()}>{"Learn More"}</a> */}
                {/* TODO: GET A BETTER LINK THANK THIS EMAIL */}
                <div>
                  {"Contact us at "}
                  <a href="mailto:success@aible.com">success@aible.com</a>
                </div>
              </div>
              <div className={styles.deployBox}>
                <img src={TableauLogo} className={styles.deployIcon} />
                <div className={styles.deployBoxHeader}>{"Tableau"}</div>
                <div className={styles.deployBoxContent}>
                  {"View What-if scenarios and predictions based on your dashboard data"}
                </div>
                <a onClick={() => goToTableauHelp()}>{"Learn More"}</a>
              </div>
              <div className={styles.deployBox}>
                <img src={PowerBILogo} className={styles.deployIcon} />
                <div className={styles.deployBoxHeader}>{"Power BI"}</div>
                <div className={styles.deployBoxContent}>
                  {"See predictions while viewing your Power BI data with the Aible Anywhere connector."}
                </div>
                <div>
                  {"Contact us at "}
                  <a href="mailto:success@aible.com">success@aible.com</a>
                </div>
              </div>

              {isEnabled("hide-bulk-scoring") === true ? (
                <div />
              ) : (
                <div className={styles.deployBox}>
                  <img src={FileLogo} className={styles.deployIcon} />
                  <div className={styles.deployBoxHeader}>{"CSV Bulk Scoring"}</div>
                  <div className={styles.deployBoxContent}>
                    {"Make predictions directly to all records in a CSV file."}
                  </div>
                  {!isDeploying && (
                    <React.Fragment>
                      <input
                        id="fileSelect"
                        type="file"
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .gz"
                        onChange={(e) => {
                          scoreFile(e.target.files[0], this.getIsMonitorScoring());
                          document.getElementById("fileSelect").value = "";
                        }}
                        style={{
                          visibility: "hidden",
                          height: "0px",
                          padding: "0px",
                          margin: "0px"
                        }}
                      />
                      {this.renderMonitorScoring()}

                      {isScoringFile ? (
                        <div>
                          <LoadingIndicator />
                          {"Scoring File..."}
                          <div>Your file will be automatically downloaded once this is complete.</div>
                        </div>
                      ) : (
                        <a onClick={() => document.getElementById("fileSelect").click()}>
                          {"Click here to score a file"}
                        </a>
                      )}
                    </React.Fragment>
                  )}
                </div>
              )}
            </div>
          </div>
          {isError ? (
            <div className={styles.deployRight}>
              <div className={styles.loading}>
                <div className={styles.loadMsg}>{"Deployment Error"}</div>
                <div className={styles.deploySubHeader}>{"Please try again or contact us for support"}</div>
              </div>
            </div>
          ) : isDeploying ? (
            <div className={styles.deployRight}>
              <div className={styles.loading}>
                <LoadingIndicator
                  style={{
                    margin: "0px",
                    marginTop: "-25vh",
                    marginBottom: "50px",
                    transform: "scale(1.75)"
                  }}
                />
                <div className={styles.loadMsg}>{"Deploying Project"}</div>
                <div className={styles.deploySubHeader}>{"This will take up to 5 minutes..."}</div>
              </div>
            </div>
          ) : (
            <div className={styles.deployRight}>
              <div>
                <div className={styles.deployHeader}>{"Custom Integrations"}</div>
                <div className={styles.deploySubHeader}>{"Add predictions to your app and data pipelines!"}</div>
                <div className={styles.deployCustomItem}>
                  <div className={styles.deployBoxHeader}>{"REST API"}</div>
                  <div className={styles.deployBoxContent}>
                    {"Generate predictions from anywhere using the Aible Predict REST API."}
                  </div>
                  <div className={styles.deployButtons}>
                    <ButtonGroup>
                      <OverlayTrigger
                        placement="top"
                        trigger="click"
                        rootClose
                        overlay={
                          <Popover id="popover-positioned-top" title="Endpoint URL">
                            <div className={styles.popList}>
                              {accountAPIEndpoint ? (
                                <React.Fragment>
                                  <div className={styles.apiHeadersLabel}>{"URL"}</div>
                                  <div>{`${accountAPIEndpoint}/v1/scores/projects/${id}`}</div>
                                </React.Fragment>
                              ) : (
                                ""
                              )}
                              {selectedTenant ? (
                                <React.Fragment>
                                  <div className={styles.apiHeadersLabel} style={{ marginTop: "10px" }}>
                                    {"Tenant"}
                                  </div>
                                  <div style={{ marginLeft: "10px" }}>{`ID: ${selectedTenant?.id}`}</div>
                                  <div style={{ marginLeft: "10px" }}>{`Name: ${selectedTenant?.fullname}`}</div>
                                </React.Fragment>
                              ) : (
                                ""
                              )}
                            </div>
                          </Popover>
                        }
                      >
                        <Button>{"View API Endpoint"}</Button>
                      </OverlayTrigger>
                    </ButtonGroup>
                  </div>
                </div>
              </div>
              <div className={styles.deployCustomItem}>
                <div className={styles.deployBoxHeader}>{"API Documentation"}</div>
                <div className={styles.deployBoxContent}>
                  {
                    "To view the current API documentation open the exported data in any client that supports OpenAPI V3 JSON."
                  }
                </div>
                <div className={styles.deployButtons}>
                  <ButtonGroup>
                    <Button
                      onClick={() => {
                        toast("OpenAPI V3 JSON copied to clipboard.", {
                          type: toast.TYPE.SUCCESS,
                          position: toast.POSITION.TOP_CENTER,
                          autoClose: 7500
                        });
                        navigator.clipboard.writeText(this.getOpenAPI(id, accountAPIEndpoint));
                      }}
                    >
                      {"Copy OpenAPI V3 JSON"}
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            </div>
          )}
        </div>
        {businessMode && this.renderDeployAdvanced()}
      </div>
    );
  }
}
